import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sheet from './Sheet';
import QtyStepper from './QtyStepper';
import { setLineQuantity, removeLine, bagCount, changeLineVariant } from './bag';
import { useBagLines, useSendBagToQuote } from './useBagLines';
import { useProducts } from './catalogApi';
import { getOptions, lineFor } from './productOptions';
import { toggleWishlist, useWishlist } from './savedLists';
import { quickAdd } from './ProductCard';
import LineImage from './LineImage';
import { formatInr, retailPrice } from './retailUtils';

/** Size picker on a bag line — swap M for L without going back to the product page. */
function SizeSelect({ line }) {
  const options = useMemo(() => getOptions(line.product), [line.product]);
  if (!options.hasSize) return null;
  const sizes = options.kind === 'combo' ? options.sizesFor(line.color) : options.sizesFor();
  if (sizes.length < 2) return null;
  const current = line.size || line.label;
  return (
    <label className="rt-line-size">
      <span className="rt-sr-only">Size for {line.product.name}</span>
      <select value={current || ''} onChange={e => {
        const next = lineFor(line.product, options, line.color, e.target.value);
        changeLineVariant(line.key, next, next.stock ?? 99);
      }}>
        {sizes.map(s => <option key={s.value} value={s.value} disabled={!s.available && s.value !== current}>
          {options.kind === 'label' ? '' : 'Size '}{s.value}{s.available ? '' : ' — sold out'}
        </option>)}
      </select>
    </label>
  );
}

export default function BagDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const wishlist = useWishlist();
  const { bag, lines, buyable, subtotal, blocked, loading } = useBagLines();
  const sendToQuote = useSendBagToQuote();
  const [confirmRemove, setConfirmRemove] = useState(null);
  const count = bagCount(bag);

  // Cross-sell: in-stock products of the same type as the first thing in the bag, not already in it.
  const first = lines.find(l => l.product)?.product;
  const { products: related } = useProducts(first ? { category: first.category, subcategory: first.subcategory } : null, 12);
  const suggestions = useMemo(() => {
    if (!first) return [];
    const inBag = new Set(bag.map(l => String(l.productId)));
    return related.filter(p => p.itemsAvailable !== 0 && !inBag.has(String(p.id))).slice(0, 6);
  }, [related, bag, first]);

  useEffect(() => { if (!open) setConfirmRemove(null); }, [open]);

  const footer = bag.length > 0 && (
    <>
      <div className="rt-sum-row" style={{ fontSize: 15 }}><span>Subtotal</span><strong style={{ color: 'var(--rt-ink)' }}>{formatInr(subtotal)}</strong></div>
      <div className="rt-tax-note" style={{ marginBottom: 12 }}>Inclusive of all taxes</div>
      {blocked && <div className="rt-alert rt-alert--error" style={{ marginBottom: 10 }}>Some items need attention before you continue.</div>}
      {/* Phase 1: the bag ends in a quote request; prepaid checkout replaces this in phase 2. */}
      <button type="button" className="rt-btn rt-btn--block" disabled={loading || blocked || !buyable.length}
        onClick={() => { onClose(); sendToQuote(buyable); }}>
        Request a quote · {formatInr(subtotal)}
      </button>
      <div className="rt-secure">We confirm the final price and delivery by email</div>
    </>
  );

  return (
    <Sheet open={open} onClose={onClose} title={`Bag${count ? ` (${count})` : ''}`} side="right" footer={footer}>
      {bag.length === 0 ? (
        <div className="rt-empty" style={{ padding: '48px 8px' }}>
          <h2>Your bag is empty</h2>
          <p>Find something you'll love.</p>
          <button type="button" className="rt-btn" style={{ marginTop: 12 }} onClick={() => { onClose(); navigate('/catalog'); }}>Start shopping</button>
        </div>
      ) : (
        <div>
          {lines.map(l => (
            <div key={l.key} className={`rt-line rt-line--compact${l.problem ? ' rt-line--bad' : ''}`}>
              <Link to={`/product/${l.productId}`} className="rt-line-img" onClick={onClose} tabIndex={-1} aria-hidden="true">
                <LineImage line={l} sizes="88px" />
              </Link>
              <div style={{ minWidth: 0 }}>
                {l.product
                  ? <Link to={`/product/${l.productId}`} className="rt-line-name" onClick={onClose}>{l.product.name}</Link>
                  : <span className="rt-line-name">{loading ? 'Loading…' : 'Unavailable product'}</span>}
                <div className="rt-line-meta">{[l.color, l.unitPrice != null ? formatInr(l.unitPrice) : null].filter(Boolean).join(' · ')}</div>
                {!loading && l.problem && <div className="rt-line-meta" role="alert">{l.problem}</div>}
                {confirmRemove === l.key ? (
                  <div className="rt-line-confirm">
                    <span>Remove this item?</span>
                    {l.product && !wishlist.some(id => String(id) === String(l.productId)) && (
                      <button type="button" className="rt-btn rt-btn--sm" onClick={() => { toggleWishlist(l.productId); removeLine(l.key); setConfirmRemove(null); }}>Move to wishlist</button>
                    )}
                    <button type="button" className="rt-btn rt-btn--ghost rt-btn--sm" onClick={() => { removeLine(l.key); setConfirmRemove(null); }}>Remove</button>
                    <button type="button" className="rt-link" onClick={() => setConfirmRemove(null)}>Keep</button>
                  </div>
                ) : (
                  <div className="rt-line-actions">
                    {l.product && <SizeSelect line={l} />}
                    {l.product && l.maxQuantity > 0 && (
                      <QtyStepper small label={`Quantity for ${l.product.name}`} value={l.quantity}
                        max={Math.max(l.maxQuantity, 1)} onChange={q => setLineQuantity(l.key, q)} />
                    )}
                    <button type="button" className="rt-text-link" onClick={() => setConfirmRemove(l.key)}>Remove</button>
                  </div>
                )}
              </div>
              <div className="rt-line-price">{!l.problem && l.lineTotal != null ? formatInr(l.lineTotal) : ''}</div>
            </div>
          ))}

          {suggestions.length > 0 && (
            <div className="rt-drawer-xsell">
              <div className="rt-eyebrow">You may also like</div>
              <div className="rt-xsell-track">
                {suggestions.map(p => (
                  <div key={p.id} className="rt-xsell-item">
                    <Link to={`/product/${p.id}`} onClick={onClose} className="rt-xsell-img">{p.thumbnailUrl && <img src={p.thumbnailUrl} srcSet={p.thumbnailSrcset} sizes="180px" alt="" loading="lazy" />}</Link>
                    <Link to={`/product/${p.id}`} onClick={onClose} className="rt-xsell-name">{p.name}</Link>
                    <div className="rt-xsell-row">
                      <span>{formatInr(retailPrice(p).price)}</span>
                      <button type="button" className="rt-text-link" aria-label={`Add ${p.name} to bag`} onClick={() => quickAdd(p)}>+ Add</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}
